import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getGetProjectionQueryKey,
  getListCashFlowsQueryKey,
  useCreateCashFlow,
  useDeleteCashFlow,
  useListCalendars,
  useListCashFlows,
  useReplaceCashFlow,
} from '../api/generated/fluence'
import type { AccountLedger, CashFlow } from '../api/generated/model'
import { formatDateShort, formatMoney } from '../lib/money'
import { blankDraft, draftFrom, toInput, type Draft } from '../lib/cashFlowDraft'
import { CashFlowForm } from './CashFlowForm'

type Mode = { kind: 'idle' } | { kind: 'new' } | { kind: 'edit'; id: string }

export function CashFlowEditor({ accounts }: { accounts: AccountLedger[] }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>({ kind: 'idle' })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)

  const flows = useListCashFlows()
  const calendars = useListCalendars()

  /**
   * A cash flow change alters the projection, so both must be refetched. This is the
   * moment TanStack Query earns its place: the chart updates because the data it depends
   * on was invalidated, not because anything told the chart to redraw.
   */
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListCashFlowsQueryKey() }),
      // Prefix match, so every horizon in the cache is refreshed, not just the visible one.
      queryClient.invalidateQueries({ queryKey: getGetProjectionQueryKey().slice(0, 1) }),
    ])
  }

  const onSettled = { onSuccess: refresh }
  const create = useCreateCashFlow({ mutation: onSettled })
  const replace = useReplaceCashFlow({ mutation: onSettled })
  const remove = useDeleteCashFlow({ mutation: onSettled })

  const busy = create.isPending || replace.isPending || remove.isPending
  const list = flows.data?.status === 200 ? flows.data.data : []
  const calendarList = calendars.data?.status === 200 ? calendars.data.data : []

  function startNew() {
    setError(null)
    const account = accounts[0]
    setDraft(blankDraft(account?.accountId ?? '', account?.currency ?? 'USD', calendarList[0]?.id ?? ''))
    setMode({ kind: 'new' })
  }

  function startEdit(flow: CashFlow) {
    setError(null)
    setDraft(draftFrom(flow))
    setMode({ kind: 'edit', id: flow.id })
  }

  function close() {
    setMode({ kind: 'idle' })
    setDraft(null)
    setError(null)
  }

  async function submit() {
    if (!draft) return
    const result = toInput(draft)
    if ('error' in result) {
      setError(result.error)
      return
    }
    setError(null)

    const response =
      mode.kind === 'edit'
        ? await replace.mutateAsync({ id: mode.id, data: result.input })
        : await create.mutateAsync({ data: result.input })

    // The generated client returns a status union rather than throwing, so a rejected
    // request has to be read off the response instead of caught.
    if (response.status === 200 || response.status === 201) {
      close()
      return
    }
    setError(problemDetail(response) ?? 'The server rejected that. Check the values and retry.')
  }

  async function confirmDelete(flow: CashFlow) {
    if (!window.confirm(`Delete "${flow.name}"? This cannot be undone.`)) return
    await remove.mutateAsync({ id: flow.id })
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Cash flows</h2>
        {mode.kind === 'idle' && (
          <button type="button" className="btn-primary btn-small" onClick={startNew}>
            Add
          </button>
        )}
      </div>

      {mode.kind !== 'idle' && draft && (
        <CashFlowForm
          draft={draft}
          setDraft={setDraft}
          accounts={accounts}
          calendars={calendarList}
          isNew={mode.kind === 'new'}
          error={error}
          busy={busy}
          onSubmit={submit}
          onCancel={close}
        />
      )}

      {flows.isPending && <p className="movements-empty">Loading…</p>}

      <ul className="cf-list">
        {list.map((flow) => (
          <li key={flow.id} className={mode.kind === 'edit' && mode.id === flow.id ? 'is-editing' : ''}>
            <div className="cf-main">
              <span className="cf-name">{flow.name}</span>
              <span className="cf-when">{describe(flow)}</span>
            </div>
            <span className={`cf-amount ${flow.direction === 'IN' ? 'is-in' : 'is-out'}`}>
              {flow.direction === 'IN' ? '+' : '−'}
              {formatMoney(flow.amountMinor, flow.currency)}
              {flow.estimation && <span className="est-tag">est</span>}
            </span>
            <div className="cf-buttons">
              <button type="button" className="btn-ghost btn-small" onClick={() => startEdit(flow)}>
                Edit
              </button>
              <button
                type="button"
                className="btn-ghost btn-small btn-danger"
                onClick={() => confirmDelete(flow)}
                disabled={busy}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** A one-line summary of when a cash flow fires, using the dates the server resolved. */
function describe(flow: CashFlow): string {
  const next = flow.nextDates.slice(0, 3).map(formatDateShort).join(', ')
  const cadence =
    flow.schedule.kind === 'EXPLICIT'
      ? 'specific dates'
      : `every ${flow.schedule.interval && flow.schedule.interval > 1 ? `${flow.schedule.interval} × ` : ''}${(flow.schedule.frequency ?? '').toLowerCase()}`
  return next ? `${cadence} · next ${next}` : cadence
}

/** Pulls the message out of an RFC 9457 problem response, if that is what came back. */
function problemDetail(response: unknown): string | null {
  if (response && typeof response === 'object' && 'data' in response) {
    const data = (response as { data: unknown }).data
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: unknown }).detail
      if (typeof detail === 'string') return detail
    }
  }
  return null
}
